import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Activity,
  Users,
  Database,
  Globe,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  HardDrive,
  Cpu,
  BarChart3,
  FileText,
  Settings,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { useAdminStats } from '@/hooks/useAdminStats';
import { formatDistanceToNow } from 'date-fns';

interface SystemMetric {
  name: string;
  value: string | number;
  change?: number;
  status: 'healthy' | 'warning' | 'critical';
  icon: React.ComponentType<{ className?: string }>;
}

interface QuickAction {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
  urgent?: boolean;
}

interface SystemAlert {
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'critical';
  timestamp: string;
}

interface RecentActivity {
  title: string;
  description: string;
  timestamp: string;
  type: 'healthy' | 'warning' | 'critical';
}

const quickActions: QuickAction[] = [
  {
    title: 'Create Organization',
    description: 'Set up a new organization',
    href: '/app/admin/organizations/new',
    icon: Globe,
    permission: 'admin.organizations.create',
  },
  {
    title: 'System Health Check',
    description: 'Run comprehensive system diagnostics',
    href: '/app/admin/system/health',
    icon: Activity,
    permission: 'admin.system.view',
  },
  {
    title: 'View Audit Logs',
    description: 'Review recent system activities',
    href: '/app/admin/audit',
    icon: FileText,
    permission: 'admin.audit.view',
  },
  {
    title: 'Manage Background Jobs',
    description: 'Monitor and manage running tasks',
    href: '/app/admin/jobs',
    icon: Zap,
    permission: 'admin.system.view',
  },
  {
    title: 'Database Administration',
    description: 'Manage database operations',
    href: '/app/admin/database',
    icon: Database,
    permission: 'admin.database.view',
  },
  {
    title: 'Global Settings',
    description: 'Configure system settings',
    href: '/app/admin/settings',
    icon: Settings,
    permission: 'admin.settings.view',
  },
];

export function AdminDashboard() {
  const { hasPermission } = useAdminPermissions();
  const [refreshing, setRefreshing] = useState(false);

  // Mock data - replace with real API calls
  const {
    systemMetrics,
    organizationStats,
    userStats,
    performanceStats,
    recentActivity,
    systemAlerts,
    isLoading,
    refetch,
  } = useAdminStats();

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const visibleQuickActions = quickActions.filter(
    action => !action.permission || hasPermission(action.permission)
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'critical':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout title="Admin Dashboard" description="System overview and administration tools">
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">System Overview</h2>
            <p className="text-sm text-muted-foreground">
              Last updated: {formatDistanceToNow(new Date(), { addSuffix: true })}
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            disabled={refreshing}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`size-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </Button>
        </div>

        {/* System Alerts */}
        {systemAlerts && systemAlerts.length > 0 && (
          <div className="space-y-3">
            {systemAlerts.map((alert: SystemAlert, index: number) => (
              <Alert
                key={index}
                variant={alert.severity === 'critical' ? 'destructive' : 'default'}
              >
                <AlertTriangle className="size-4" />
                <AlertTitle>{alert.title}</AlertTitle>
                <AlertDescription>{alert.message}</AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">System Status</CardTitle>
              <Activity className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <div className="text-2xl font-bold">{systemMetrics?.status || 'Healthy'}</div>
                <Badge className={getStatusBadgeColor(systemMetrics?.status || 'healthy')}>
                  {systemMetrics?.uptime || '99.9%'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Uptime: {systemMetrics?.uptimeFormatted || '15 days, 4 hours'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Organizations</CardTitle>
              <Globe className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{organizationStats?.total || 0}</div>
              <p className="text-xs text-muted-foreground">
                {organizationStats?.active || 0} active, {organizationStats?.inactive || 0} inactive
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats?.total || 0}</div>
              <p className="text-xs text-muted-foreground">
                {userStats?.activeToday || 0} active today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Database Size</CardTitle>
              <Database className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{performanceStats?.dbSize || '0 GB'}</div>
              <p className="text-xs text-muted-foreground">
                {performanceStats?.dbGrowth || '+2.1%'} from last week
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Performance Metrics */}
        {hasPermission('admin.system.view') && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Cpu className="size-5" />
                  <span>System Performance</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>CPU Usage</span>
                    <span>{performanceStats?.cpu || 45}%</span>
                  </div>
                  <Progress value={performanceStats?.cpu || 45} className="h-2" />
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>Memory Usage</span>
                    <span>{performanceStats?.memory || 67}%</span>
                  </div>
                  <Progress value={performanceStats?.memory || 67} className="h-2" />
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>Disk Usage</span>
                    <span>{performanceStats?.disk || 23}%</span>
                  </div>
                  <Progress value={performanceStats?.disk || 23} className="h-2" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="size-5" />
                  <span>Usage Analytics</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">API Requests (24h)</p>
                      <p className="text-xs text-muted-foreground">
                        {performanceStats?.apiRequests || '12,483'} requests
                      </p>
                    </div>
                    <TrendingUp className="size-8 text-green-600" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Background Jobs</p>
                      <p className="text-xs text-muted-foreground">
                        {performanceStats?.backgroundJobs || '23'} queued
                      </p>
                    </div>
                    <Clock className="size-8 text-blue-600" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Cache Hit Rate</p>
                      <p className="text-xs text-muted-foreground">
                        {performanceStats?.cacheHitRate || '94.2%'} efficiency
                      </p>
                    </div>
                    <CheckCircle className="size-8 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {visibleQuickActions.map(action => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.href}
                    variant="outline"
                    className="h-auto justify-start p-4"
                    asChild
                  >
                    <a href={action.href} className="flex items-start space-x-3">
                      <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                      <div className="text-left">
                        <div className="flex items-center space-x-2 font-medium">
                          <span>{action.title}</span>
                          {action.urgent && (
                            <Badge variant="destructive" className="text-xs">
                              Urgent
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">{action.description}</div>
                      </div>
                      <ExternalLink className="ml-auto size-4 text-muted-foreground" />
                    </a>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        {hasPermission('admin.audit.view') && recentActivity && (
          <Card>
            <CardHeader>
              <CardTitle>Recent System Activity</CardTitle>
              <CardDescription>Latest administrative actions and system events</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.slice(0, 5).map((activity: RecentActivity, index: number) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 border-b pb-3 last:border-b-0"
                  >
                    <div className={`rounded-full p-2 ${getStatusBadgeColor(activity.type)}`}>
                      <Activity className="size-3" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{activity.title}</p>
                      <p className="text-xs text-muted-foreground">{activity.description}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(activity.timestamp), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
